import { httpClient } from '@/api/http-client';
import type {
  PartnerDashboard,
  PartnerStats,
  Payout,
  Campaign,
  PartnerProfile,
  CreatePayoutRequest,
  PaginatedCommissions,
  PaginatedPayouts,
} from '../types/partner';

export const partnerService = {
  async getDashboard(): Promise<PartnerDashboard> {
    try {
      return await httpClient.get<PartnerDashboard>('/partners/me/dashboard');
    } catch {
      return {
        balance: { available: 0, pending: 0, total: 0 },
        stats: { totalReferrals: 0, totalConversions: 0, conversionRate: 0, totalClicks: 0 },
        recentCommissions: [],
        recentPayouts: [],
      } as any;
    }
  },

  async getBalance(): Promise<{ available: number; pending: number; total: number }> {
    try {
      return await httpClient.get<{ available: number; pending: number; total: number }>('/partners/me/balance');
    } catch {
      return { available: 0, pending: 0, total: 0 };
    }
  },

  async getStats(period?: string): Promise<PartnerStats> {
    const params = period ? `?period=${period}` : '';
    return httpClient.get<PartnerStats>(`/partners/me/stats${params}`);
  },

  async getCommissions(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedCommissions> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    const res = await httpClient.get<any>(`/commissions/me${query ? `?${query}` : ''}`);
    const page = res.page || params?.page || 1;
    const pageSize = res.limit || res.pageSize || params?.limit || 10;
    const total = res.total || 0;
    return {
      items: res.items || [],
      total,
      page,
      pageSize,
      hasMore: res.hasMore ?? (page * pageSize < total),
    };
  },

  async requestPayout(data: CreatePayoutRequest): Promise<Payout> {
    const res = await httpClient.post<any>('/payouts/me', data);
    return res.payout || res;
  },

  async getPayouts(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedPayouts> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return httpClient.get<PaginatedPayouts>(`/payouts/me${query ? `?${query}` : ''}`);
  },

  async getCampaigns(): Promise<Campaign[]> {
    return httpClient.get<Campaign[]>('/campaigns/me');
  },

  async getProfile(): Promise<PartnerProfile> {
    return httpClient.get<PartnerProfile>('/partners/me');
  },

  async updateProfile(data: {
    name?: string;
    phone?: string;
    pixKey?: string;
  }): Promise<PartnerProfile> {
    return httpClient.patch<PartnerProfile>('/partners/me', data);
  },

  async addPixKey(data: {
    type?: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
    pixKeyType?: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
    key?: string;
    pixKey?: string;
  }): Promise<{ success: boolean }> {
    const type = data.type || data.pixKeyType || 'CPF';
    const key = data.key || data.pixKey || '';
    return httpClient.post<{ success: boolean }>('/partners/me/pix-keys', {
      type,
      key,
      pixKeyType: type,
      pixKey: key,
    });
  },

  async addBankAccount(data: {
    bankName: string;
    bankCode: string;
    agency: string;
    account: string;
    accountType: 'CHECKING' | 'SAVINGS';
    holderName: string;
    holderCpf: string;
  }): Promise<{ success: boolean }> {
    return httpClient.post<{ success: boolean }>('/partners/me/bank-accounts', data);
  },

  async generateDeepLink(serviceType: 'TELEMEDICINE' | 'HOME_VISIT'): Promise<{ deepLink: string }> {
    const res = await httpClient.post<any>('/partners/me/deep-links', { serviceType });
    return { deepLink: res.deepLink || res.link || '' };
  },

  async getDeepLinks(): Promise<{ deepLinks: Array<{ id: string; serviceType: string; deepLink: string; createdAt: string }> }> {
    return httpClient.get<{ deepLinks: Array<{ id: string; serviceType: string; deepLink: string; createdAt: string }> }>('/partners/me/deep-links');
  },
};
